import { useState } from "react";
import { usePortfolio } from "../lib/usePortfolio";
import { inr, signed, tone, istDateTime, fmt } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Panel, PageHeader, Metric, th, td, Empty } from "../components/Panel";
import { Odometer, LiquidFill } from "../components/motion";

const TYPES = [["ALL", "All entries"], ["CAPITAL", "Capital"], ["REALISED_PNL", "Realised P&L"], ["CHARGES", "Charges"], ["DELIVERY_BUY", "Delivery buy"], ["DELIVERY_SELL", "Delivery sell"], ["MTM_SETTLEMENT", "MTM settlement"], ["EXPIRY_SETTLEMENT", "Expiry settlement"]];

export default function Funds() {
  const f = usePortfolio("/funds", 5000);
  const [type, setType] = useState("ALL");
  const ledger = usePortfolio(type === "ALL" ? "/ledger" : `/ledger?type=${type}`) || [];
  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="funds-page">
      <PageHeader title="Funds" />
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4 items-start">
        <Panel title="Equity & commodity · virtual">
          <div className="grid grid-cols-2 divide-x divide-border border-b border-border">
            <Metric label="Available margin" value={inr(f?.available_margin)} testid="funds-available-margin" />
            <Metric label="Used margin" value={inr(f?.used_margin)} testid="funds-used-margin" />
          </div>
          <div className="grid grid-cols-2 divide-x divide-border">
            <Metric label="Opening capital" value={inr(f?.opening_balance)} testid="funds-opening-balance" />
            <Metric label="Cash balance (ledger)" value={<Odometer text={inr(f?.cash_balance)} testid="funds-balance-odometer" />} testid="funds-cash-balance" />
          </div>
          <div className="border-t border-border"><LiquidFill used={f?.used_margin || 0} total={(f?.used_margin || 0) + (f?.available_margin || 0)} /></div>
        </Panel>
        <Panel title="Breakdown">
          <div className="px-4 py-2 text-[12.5px] divide-y divide-border/60">
            {[["Intraday margin (MIS equity)", inr(f?.margin_split?.intraday)], ["SPAN + exposure (futures, option selling)", inr(f?.margin_split?.span_exposure)],
              ["Option premium (buying)", inr(f?.margin_split?.premium)], ["Delivery bought today (CNC)", inr(f?.delivery_today)], ["Blocked for open orders", inr(f?.order_margin)],
              ["Realised P&L today", <span className={tone(f?.realised_pnl_today)}>{signed(f?.realised_pnl_today)}</span>],
              ["Unrealised MTM", <span className={tone(f?.unrealised_mtm)}>{signed(f?.unrealised_mtm)}</span>],
              ["Charges paid today", inr(f?.charges_today)]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2"><span className="text-muted-foreground">{k}</span><span className="num">{v}</span></div>))}
          </div>
        </Panel>
      </div>
      <Panel title="Ledger statement" right={
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-7 w-40 text-[11px]" data-testid="ledger-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>}>
        {!ledger.length ? <Empty>No ledger entries.</Empty> : (
          <div className="overflow-x-auto thin-scroll max-h-[520px]">
            <table className="w-full" data-testid="ledger-table">
              <thead className="border-b border-border sticky top-0 bg-card"><tr>{["Date", "Type", "Description", "Amount", "Balance"].map((h, i) => <th key={h} className={`${th} ${i > 2 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
              <tbody>{ledger.map((e) => (
                <tr key={e.id} className="border-b border-border/60">
                  <td className={`${td} num text-muted-foreground`}>{istDateTime(e.at)}</td>
                  <td className={`${td} text-[10.5px] font-semibold tracking-wide`}>{e.type.replace("_", " ")}</td>
                  <td className={`${td} max-w-[360px] truncate`}>{e.note}</td>
                  <td className={`${td} num text-right ${tone(e.amount)}`}>{signed(e.amount)}</td>
                  <td className={`${td} num text-right`}>{fmt(e.balance_after)}</td>
                </tr>))}</tbody>
            </table>
          </div>)}
      </Panel>
      <p data-testid="funds-virtual-label" className="text-[10.5px] text-muted-foreground">Virtual balance – not real money</p>
    </div>
  );
}
